/**
 * The page's Back button: an outlined icon button (↩). A page header from the CPV kit draws it at its right end
 * (placement="inline", registering as the host); every other page gets it at the start of the top bar's breadcrumb.
 * Exactly one is shown. Where it goes, most specific first:
 *   1. a page header's own target (components/PageHeader `back="/path"` or `back={fn}`, via usePageHeaderBack);
 *   2. the page's declared target (usePageReturn(...)); usePageReturn(null) hides the button;
 *   3. the logical parent — the menu item the page sits under (menuParent), home for a menu page;
 *   4. history back, or home when there is no in-app history (a deep link opened in a fresh tab).
 * A plain path target returns to that list's last filters and page (listReturn.js).
 * Hidden on the home page itself. Styled from the CPV UI kit tokens.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from 'antd';
import { Undo2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HOME_PATH } from '@/config';
import { useCpvTokens } from '@/components/cpv-ui/useCpvTokens';
import { SPACE } from '@/components/cpv-ui/tokens';
import { usePageChrome } from './PageChromeContext';
import { menuParent } from './menuParent';
import { withListSearch } from './listReturn';
import './PageReturn.css';

const DEFAULT_RETURN = -1;

export default function PageReturn({ navigation, label = 'Back', placement = 'bar' }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { returnTo, headerBack, returnHosts, registerReturnHost } = usePageChrome();
  const tokens = useCpvTokens();
  const inline = placement === 'inline';
  useEffect(() => (inline ? registerReturnHost() : undefined), [inline, registerReturnHost]);
  if (!inline && returnHosts > 0) return null;
  if (pathname === '/' || pathname === HOME_PATH) return null;
  if (headerBack === undefined && (returnTo === null || returnTo === false)) return null;

  const historyBack = () => ((window.history.state?.idx ?? 0) === 0 ? navigate(HOME_PATH, { replace: true }) : navigate(-1));
  const go = (target) => {
    if (typeof target === 'function') return target();
    if (typeof target === 'string') return navigate(withListSearch(target));
    if (typeof target === 'number') return target === DEFAULT_RETURN ? historyBack() : navigate(target);
    if (target && typeof target === 'object' && target.to !== undefined) {
      return navigate(target.to, { state: target.state, replace: target.replace });
    }
    return historyBack();
  };

  const onClick = (e) => {
    e.preventDefault();
    if (headerBack !== undefined) return go(headerBack);
    if (returnTo !== DEFAULT_RETURN) return go(returnTo);
    const parent = menuParent(pathname, navigation, HOME_PATH);
    return parent ? navigate(withListSearch(parent)) : historyBack();
  };

  return (
    <div className={`flex items-center shrink-0 page-return page-return--${inline ? 'inline' : 'bar'}`} data-testid="page-return" style={tokens.vars}>
      <Tooltip title={label}>
        <Button aria-label={label} onClick={onClick} icon={<Undo2 size={SPACE.lg + SPACE.xs} aria-hidden="true" />} className="page-return-btn" />
      </Tooltip>
    </div>
  );
}

PageReturn.propTypes = { navigation: PropTypes.object, label: PropTypes.string, placement: PropTypes.oneOf(['bar', 'inline']) };
